/** A point in plan: X across the room, Z along it. */
export interface IPlanPoint {
    x: number;
    z: number;
}

/** A line in plan through `point` along `direction` (not normalised). */
export interface IPlanLine {
    point: IPlanPoint;
    direction: IPlanPoint;
}

export function planSub(left: IPlanPoint, right: IPlanPoint): IPlanPoint {
    return { x: left.x - right.x, z: left.z - right.z };
}

export function planAdd(a: IPlanPoint, b: IPlanPoint, scale: number = 1): IPlanPoint {
    return { x: a.x + b.x * scale, z: a.z + b.z * scale };
}

export function planDot(a: IPlanPoint, b: IPlanPoint): number {
    return a.x * b.x + a.z * b.z;
}

export function planLength(a: IPlanPoint): number {
    return Math.hypot(a.x, a.z);
}

export function planUnit(a: IPlanPoint): IPlanPoint {
    const length: number = planLength(a);
    return length > 1e-12 ? { x: a.x / length, z: a.z / length } : { x: 0, z: 0 };
}

/** The mean of the vertices — inside any convex polygon. */
export function planCentroid(points: readonly IPlanPoint[]): IPlanPoint {
    const sum: IPlanPoint = points.reduce((acc: IPlanPoint, point: IPlanPoint) => planAdd(acc, point), { x: 0, z: 0 });
    return { x: sum.x / points.length, z: sum.z / points.length };
}

/** Unit normal of the segment a → b that points to the side where `toward` lies. */
export function planNormalToward(a: IPlanPoint, b: IPlanPoint, toward: IPlanPoint): IPlanPoint {
    const along: IPlanPoint = planUnit(planSub(b, a));
    const normal: IPlanPoint = { x: -along.z, z: along.x };
    return planDot(normal, planSub(toward, a)) >= 0 ? normal : { x: -normal.x, z: -normal.z };
}

/** Where two lines cross; null when they are parallel. */
export function planIntersect(first: IPlanLine, second: IPlanLine): IPlanPoint | null {
    const cross: number = first.direction.x * second.direction.z - first.direction.z * second.direction.x;
    if (Math.abs(cross) < 1e-12) {
        return null;
    }
    const delta: IPlanPoint = planSub(second.point, first.point);
    const t: number = (delta.x * second.direction.z - delta.z * second.direction.x) / cross;
    return planAdd(first.point, first.direction, t);
}

/**
 * Keeps the part of a convex polygon where `(p − point) · normal ≥ 0` (Sutherland–Hodgman
 * against one line). The result stays convex and keeps the vertex order.
 */
export function clipByHalfPlane(polygon: readonly IPlanPoint[], point: IPlanPoint, normal: IPlanPoint): IPlanPoint[] {
    const side: (p: IPlanPoint) => number = (p: IPlanPoint): number => planDot(planSub(p, point), normal);
    const result: IPlanPoint[] = [];
    for (let i: number = 0; i < polygon.length; i++) {
        const current: IPlanPoint = polygon[i];
        const next: IPlanPoint = polygon[(i + 1) % polygon.length];
        const sideCurrent: number = side(current);
        const sideNext: number = side(next);
        const keepsCurrent: boolean = sideCurrent >= 0;
        const keepsNext: boolean = sideNext >= 0;
        if (keepsCurrent) {
            result.push(current);
        }
        if (keepsCurrent !== keepsNext) {
            const t: number = sideCurrent / (sideCurrent - sideNext);
            result.push(planAdd(current, planSub(next, current), t));
        }
    }
    return result;
}

/** Twice the signed area of a polygon: positive when it winds from +X toward +Z. */
function doubleSignedArea(polygon: readonly IPlanPoint[]): number {
    let sum: number = 0;
    for (let i: number = 0; i < polygon.length; i++) {
        const a: IPlanPoint = polygon[i];
        const b: IPlanPoint = polygon[(i + 1) % polygon.length];
        sum += a.x * b.z - b.x * a.z;
    }
    return sum;
}

/** The area of a simple polygon, either winding. */
export function planArea(polygon: readonly IPlanPoint[]): number {
    return Math.abs(doubleSignedArea(polygon)) / 2;
}

/** The length of a closed outline. */
export function planPerimeter(polygon: readonly IPlanPoint[]): number {
    let sum: number = 0;
    for (let i: number = 0; i < polygon.length; i++) {
        sum += planLength(planSub(polygon[(i + 1) % polygon.length], polygon[i]));
    }
    return sum;
}

/** Whether a polygon turns the same way at every vertex (straight runs allowed). */
export function isConvexPlan(polygon: readonly IPlanPoint[]): boolean {
    let sign: number = 0;
    for (let i: number = 0; i < polygon.length; i++) {
        const a: IPlanPoint = polygon[i];
        const b: IPlanPoint = polygon[(i + 1) % polygon.length];
        const c: IPlanPoint = polygon[(i + 2) % polygon.length];
        const turn: number = Math.sign((b.x - a.x) * (c.z - b.z) - (b.z - a.z) * (c.x - b.x));
        if (turn !== 0 && sign !== 0 && turn !== sign) {
            return false;
        }
        sign = turn === 0 ? sign : turn;
    }
    return true;
}

/** The convex hull of a point set (Andrew's monotone chain). */
export function planConvexHull(points: readonly IPlanPoint[]): IPlanPoint[] {
    const sorted: IPlanPoint[] = [...points].sort((a: IPlanPoint, b: IPlanPoint) => a.x - b.x || a.z - b.z);
    const cross: (o: IPlanPoint, a: IPlanPoint, b: IPlanPoint) => number = (o: IPlanPoint, a: IPlanPoint, b: IPlanPoint): number =>
        (a.x - o.x) * (b.z - o.z) - (a.z - o.z) * (b.x - o.x);
    const half: (list: readonly IPlanPoint[]) => IPlanPoint[] = (list: readonly IPlanPoint[]): IPlanPoint[] => {
        const chain: IPlanPoint[] = [];
        for (const point of list) {
            while (chain.length >= 2 && cross(chain[chain.length - 2], chain[chain.length - 1], point) <= 0) {
                chain.pop();
            }
            chain.push(point);
        }
        chain.pop();
        return chain;
    };
    return [...half(sorted), ...half([...sorted].reverse())];
}

/**
 * The part of `subject` inside the convex polygon `clip` (Sutherland–Hodgman over each edge of
 * `clip`). The subject may be concave; an empty array when they do not overlap.
 */
export function clipByConvex(subject: readonly IPlanPoint[], clip: readonly IPlanPoint[]): IPlanPoint[] {
    const inside: IPlanPoint = planCentroid(clip);
    let result: IPlanPoint[] = [...subject];
    for (let i: number = 0; i < clip.length && result.length > 0; i++) {
        const a: IPlanPoint = clip[i];
        const b: IPlanPoint = clip[(i + 1) % clip.length];
        if (planLength(planSub(b, a)) > 1e-9) {
            result = clipByHalfPlane(result, a, planNormalToward(a, b, inside));
        }
    }
    return result;
}
